import { LiveData, Store } from '@toeverything/infra';
import { map, of, switchMap } from 'rxjs';

import type { DefaultServerService } from '../../cloud';
import { UserDBService } from '../../userspace';
import type { UserDBWithTables } from '../../userspace/entities/user-db';
import type { AccountTemplateMeta } from '../type';

/**
 * Metadata of the account-level template library, backed by the userspace
 * `accountTemplate` table — per account, synced across devices.
 */
export class AccountTemplateListStore extends Store {
  constructor(private readonly defaultServerService: DefaultServerService) {
    super();
  }

  private _db$: LiveData<UserDBWithTables | null> | null = null;

  /**
   * Resolved lazily: `DefaultServerService` throws when no server is
   * registered yet, and that must not break the service graph at boot.
   */
  private get db$(): LiveData<UserDBWithTables | null> {
    if (!this._db$) {
      try {
        const userDBService =
          this.defaultServerService.server.scope.get(UserDBService);
        this._db$ = userDBService.currentUserDB.db$;
      } catch {
        this._db$ = new LiveData<UserDBWithTables | null>(null);
      }
    }
    return this._db$;
  }

  /** false when signed out — the shared library is hidden then */
  available$ = LiveData.computed(get => !!get(this.db$));

  isLoading$ = LiveData.computed(get => {
    const db = get(this.db$);
    if (!db) return false;
    return !get(db.accountTemplate.isLoaded$);
  });

  watchTemplates() {
    return this.db$.pipe(
      switchMap(db => {
        if (!db) return of([] as AccountTemplateMeta[]);
        return db.accountTemplate
          .find$()
          .pipe(map(rows => rows as AccountTemplateMeta[]));
      })
    );
  }

  getTemplates(): AccountTemplateMeta[] {
    const db = this.db$.value;
    if (!db) return [];
    return db.accountTemplate.find() as AccountTemplateMeta[];
  }

  get(id: string): AccountTemplateMeta | undefined {
    const db = this.db$.value;
    if (!db) return undefined;
    return (db.accountTemplate.get(id) as AccountTemplateMeta | null) ?? undefined;
  }

  /** the yjs orm adapter upserts on `create` */
  upsert(meta: AccountTemplateMeta) {
    const db = this.db$.value;
    if (!db) throw new Error('Not signed in');
    db.accountTemplate.create(meta);
  }

  update(id: string, patch: Partial<Omit<AccountTemplateMeta, 'id'>>) {
    const db = this.db$.value;
    if (!db) throw new Error('Not signed in');
    db.accountTemplate.update(id, patch);
  }

  remove(id: string) {
    const db = this.db$.value;
    if (!db) throw new Error('Not signed in');
    db.accountTemplate.delete(id);
  }
}
